import { SectionTitles } from '@/libs/core/src/lib/enums/SectionTitles.enum';
import {
  checkIfCondition,
  convertYesNoToDescriptive,
  getQuestionDisplayValue,
} from '@/libs/core/src/lib/functions/buildFlexibleDisplay';
import {
  type QuestionAnswer,
  type QuestionOption,
  type TextItem,
} from '@/libs/core/src/lib/types/ComplaintSummary.type';

import {
  CCSPrimaryQuestionIds,
  CCSSecondaryQuestionIds,
} from '@chiroup/core/constants/CCSQuestionIds';
import { escapeHtml } from '@chiroup/core/functions/sanitizeChatHtml';

// Legacy types that are used internally
type SummarySection = {
  title: string;
  items?: SummaryItem[];
  mainQuestionAnswerMap?: Record<string, QuestionAnswer>;
  html?: string;
};

type SummaryItem = TextItem | SummarySection;

type ListItem = string | { nested: string[]; title?: string };

type QAHolder = { questionAnswerMap?: Record<string, QuestionAnswer> };

// Unified formatting helpers
const bold = (s: string) => `<strong>${s}</strong>`;

const labelValue = (label: string, value?: string) => {
  const v = (value || '').trim();

  return v ? `${label}: ${bold(v)}` : '';
};

// Build the Auto accident details block HTML using QA map
export function buildAutoAccidentDetailsHtml(qa: Record<string, QuestionAnswer>): string {
  const cause = 'auto accident';
  let type = getFirstAnswered(
    qa,
    CCSPrimaryQuestionIds.descriptionOfAccidentQuestionId,
    CCSSecondaryQuestionIds.descriptionOfAccidentQuestionId
  )[0];

  if (!type) {
    const qd =
      qa[CCSPrimaryQuestionIds.descriptionOfAccidentQuestionId] ||
      qa[CCSSecondaryQuestionIds.descriptionOfAccidentQuestionId];
    const disp = qd
      ? getQuestionDisplayValue(
          qd === qa[CCSPrimaryQuestionIds.descriptionOfAccidentQuestionId]
            ? CCSPrimaryQuestionIds.descriptionOfAccidentQuestionId
            : CCSSecondaryQuestionIds.descriptionOfAccidentQuestionId,
          qd as any
        )
      : '';

    if (disp && disp !== '[No response]') {
      type = Array.isArray(disp) ? String(disp[0]) : String(disp);
    }
  }

  const secondary = getFirstAnswered(
    qa,
    CCSPrimaryQuestionIds.secondaryCollisionsQuestionId,
    CCSSecondaryQuestionIds.secondaryCollisionsQuestionId
  )[0];
  const sitting = getFirstAnswered(
    qa,
    CCSPrimaryQuestionIds.vehicleSittingQuestionId,
    CCSSecondaryQuestionIds.vehicleSittingQuestionId
  )[0];
  const vehicleType = getFirstAnswered(
    qa,
    CCSPrimaryQuestionIds.vehicleTypeQuestionId,
    CCSSecondaryQuestionIds.vehicleTypeQuestionId
  )[0];
  const speed = getFirstAnswered(
    qa,
    CCSPrimaryQuestionIds.speedEstimateQuestionId,
    CCSSecondaryQuestionIds.speedEstimateQuestionId
  )[0];
  const otherVehicleType = getFirstAnswered(
    qa,
    CCSPrimaryQuestionIds.otherVehicleTypeQuestionId,
    CCSSecondaryQuestionIds.otherVehicleTypeQuestionId
  )[0];
  const otherVehicleSpeed = getFirstAnswered(
    qa,

    CCSPrimaryQuestionIds.otherSpeedEstimateQuestionRadioId,
    CCSSecondaryQuestionIds.otherSpeedEstimateQuestionRadioId
  )[0];

  const damage = getFirstAnswered(
    qa,
    CCSPrimaryQuestionIds.vehicleDamageQuestionId,
    CCSSecondaryQuestionIds.vehicleDamageQuestionId
  )[0];
  const drivable = getFirstAnswered(
    qa,
    CCSPrimaryQuestionIds.vehicleDrivableQuestionId,
    CCSSecondaryQuestionIds.vehicleDrivableQuestionId
  )[0];
  const road = getFirstAnswered(
    qa,
    CCSPrimaryQuestionIds.roadConditionsQuestionId,
    CCSSecondaryQuestionIds.roadConditionsQuestionId
  )[0];
  const looking = getFirstAnswered(
    qa,
    CCSPrimaryQuestionIds.impactLookingQuestionId,
    CCSSecondaryQuestionIds.impactLookingQuestionId
  )[0];
  const braced = getFirstAnswered(
    qa,
    CCSPrimaryQuestionIds.autoWereYouQuestionId,
    CCSSecondaryQuestionIds.autoWereYouQuestionId
  )[0];
  const seatbelt = getFirstAnswered(
    qa,
    CCSPrimaryQuestionIds.seatbeltQuestionId,
    CCSSecondaryQuestionIds.seatbeltQuestionId
  )[0];
  const airbag = getFirstAnswered(
    qa,
    CCSPrimaryQuestionIds.airbagQuestionId,
    CCSSecondaryQuestionIds.airbagQuestionId
  )[0];
  const noticed = getFirstAnswered(
    qa,
    CCSPrimaryQuestionIds.noticeSymptomsQuestionId,
    CCSSecondaryQuestionIds.noticeSymptomsQuestionId
  )[0];
  const sought = getFirstAnswered(
    qa,
    CCSPrimaryQuestionIds.seekCareQuestionId,
    CCSSecondaryQuestionIds.seekCareQuestionId
  )[0];

  const sittingSeat = sitting ? `${sitting} seat` : '';
  const vehicleTypeLower = vehicleType ? vehicleType.toLowerCase() : '';
  const seatbeltPhrase =
    seatbelt === 'Yes' ? 'wearing seatbelt' : seatbelt === 'No' ? 'not wearing seatbelt' : '';
  const airbagPhrase =
    airbag === 'Yes' ? 'air bag deployed' : airbag === 'No' ? 'no air bag deployed' : '';
  const drivablePhrase =
    drivable === 'Yes'
      ? 'drivable'
      : drivable === 'No'
        ? 'not drivable'
        : drivable === 'Drivable'
          ? 'drivable'
          : drivable === 'Not drivable'
            ? 'not drivable'
            : '';

  const lis: string[] = [];
  // For auto accidents, show the type (e.g., "side impact") instead of "auto accident"
  const dueLine = `Due to: ${bold(type || cause)}`;
  lis.push(dueLine);

  if (secondary) lis.push(`Secondary collision: ${bold(secondary)}`);

  // Patient vehicle information grouped together
  if (sittingSeat || vehicleTypeLower || speed) {
    const line = `Patient vehicle: ${sittingSeat ? bold(sittingSeat) : ''}${
      sittingSeat && (vehicleTypeLower || speed) ? ' in a ' : ''
    }${vehicleTypeLower ? bold(vehicleTypeLower) : ''}${vehicleTypeLower && speed ? ', ' : ''}${
      speed ? `traveling at ${bold(`${String(speed)} mph`)}` : ''
    }`;
    lis.push(line);
  }

  if (damage) lis.push(`Patient vehicle damage: ${bold(damage)}`);
  if (drivablePhrase) lis.push(`Vehicle drivable: ${bold(drivablePhrase)}`);

  // Other vehicle information grouped together
  if (otherVehicleType) lis.push(`Other vehicle: ${bold(otherVehicleType)}`);
  if (otherVehicleSpeed) lis.push(`Other vehicle speed: ${bold(otherVehicleSpeed)}`);

  if (road) lis.push(`Road conditions: ${bold(road)}`);
  const statusBits = [
    looking ? `looking ${String(looking).toLowerCase()}` : '',
    braced ? String(braced).toLowerCase() : '',
    seatbeltPhrase,
    airbagPhrase,
  ].filter(Boolean);

  if (statusBits.length) {
    const statusLine = `Status at time of impact: ${bold(statusBits.join(', '))}`;
    lis.push(statusLine);
  }

  if (noticed) lis.push(`Symptoms noticed: ${bold(noticed)}`);
  if (sought) lis.push(`First sought care: ${bold(sought)}`);

  return lis.length ? `<ul>\n${lis.map(l => `<li>${l}</li>`).join('\n')}\n</ul>` : '';
}

// Build the Work injury details block HTML using QA map
export function buildWorkInjuryDetailsHtml(qa: Record<string, QuestionAnswer>): string {
  const description = getFirstAnswered(
    qa,
    CCSPrimaryQuestionIds.descriptionInjuryQuestionId,
    CCSSecondaryQuestionIds.descriptionInjuryQuestionId
  )[0];

  const location = getFirstAnswered(
    qa,
    CCSPrimaryQuestionIds.whereDidInjuryQuestionId,
    CCSSecondaryQuestionIds.whereDidInjuryQuestionId
  )[0];

  const didReport = getFirstAnswered(
    qa,
    CCSPrimaryQuestionIds.didYouReportInjuryQuestionId,
    CCSSecondaryQuestionIds.didYouReportInjuryQuestionId
  )[0];

  const reportedTo = getFirstAnswered(
    qa,
    CCSPrimaryQuestionIds.whoReportInjuryQuestionId,
    CCSSecondaryQuestionIds.whoReportInjuryQuestionId
  )[0];

  const lostWork = getFirstAnswered(
    qa,
    CCSPrimaryQuestionIds.lostWorkInjuryQuestionId,
    CCSSecondaryQuestionIds.lostWorkInjuryQuestionId
  )[0];

  const lostWorkFreq = getFirstAnswered(
    qa,
    CCSPrimaryQuestionIds.howOftenLostWorkInjuryQuestionId,
    CCSSecondaryQuestionIds.howOftenLostWorkInjuryQuestionId
  )[0];

  const currentlyOff = getFirstAnswered(
    qa,
    CCSPrimaryQuestionIds.currentlyOffWorkInjuryQuestionId,
    CCSSecondaryQuestionIds.currentlyOffWorkInjuryQuestionId
  )[0];

  const contactPerson = getFirstAnswered(
    qa,
    CCSPrimaryQuestionIds.contactPersonQuestionId,
    CCSSecondaryQuestionIds.contactPersonQuestionId
  )[0];

  const contactInfo = getFirstAnswered(
    qa,
    CCSPrimaryQuestionIds.contactPersonInfoQuestionId,
    CCSSecondaryQuestionIds.contactPersonInfoQuestionId
  )[0];

  const lis: string[] = [];

  if (description) {
    lis.push(`Work injury description: ${bold(description)}`);
  }

  if (location) {
    lis.push(`Location: ${bold(location)}`);
  }

  if (didReport) {
    lis.push(`Did you report: ${bold(didReport)}`);

    if (didReport === 'Yes' && reportedTo) {
      lis.push(`Reported to: ${bold(reportedTo)}`);
    }
  }

  if (lostWork) {
    lis.push(`Lost work: ${bold(lostWork)}`);

    if (lostWork === 'Yes' && lostWorkFreq) {
      lis.push(`Lost work frequency: ${bold(lostWorkFreq)}`);
    }
  }

  if (currentlyOff) {
    lis.push(`Currently off work: ${bold(currentlyOff)}`);
  }

  if (contactPerson) {
    lis.push(`Employer contact person: ${bold(contactPerson)}`);

    if (contactInfo) {
      lis.push(`Employer contact: ${bold(contactInfo)}`);
    }
  }

  return lis.length ? `<ul>\n${lis.map(l => `<li>${l}</li>`).join('\n')}\n</ul>` : '';
}

// Shared regexes for normalization/cleanup
const RE_UUID = /\{[a-f0-9-]+\}/g;
const RE_SPAN_DATA_QID = /<span[^>]*data-question-id[^>]*>([^<]+)<\/span>/g;
const RE_DOUBLE_COMMA = /,\s*,/g;
const RE_TRAILING_COMMA = /,\s*$/;
const RE_COMMA_BEFORE_PUNCT = /,\s*([.!?])/g;
const RE_WHITESPACE = /\s+/g;
const RE_COLON_UNDEFINED = /\s*:\s*undefined/g;
const RE_COLON_NO_RESPONSE = /\s*:\s*\[No response\]/g;
const RE_COLON_NONE = /\s*:\s*None/g;
const RE_EMPTY_COLON = /\s*:\s*$/g;
const RE_TRAILING_DESCRIBED = /\s+described as\s*$/;
const RE_TRAILING_CHARACTERIZED = /\s+characterized as\s*$/;
const RE_TRAILING_PRESENTED = /\s+presented as\s*$/;

function isTextItem(item: SummaryItem): item is TextItem {
  return (item as any)?.template !== undefined;
}

function renderTemplatePlain(template: string, qam: Record<string, QuestionAnswer>): string {
  if (!template) return '';

  return template.replace(/\{([^}]+)\}/g, (_m, qid) => {
    const qd = qam?.[qid];
    if (!qd) return '';
    const display = getQuestionDisplayValue(qid, qd);
    if (!display || display === '[No response]') return '';

    // Always bold the resolved answer values
    return Array.isArray(display)
      ? display.map(v => bold(String(v))).join(', ')
      : bold(String(display));
  });
}

function resolveWithDisplay(
  template: string,
  qam: Record<string, QuestionAnswer>
): { text: string; complete: boolean } {
  if (!template) return { text: '', complete: false };
  const placeholderRegex = /\{([^}]+)\}/g;
  const questionIds: string[] = [];
  template.replace(placeholderRegex, (_m, qid) => {
    questionIds.push(qid);

    return '';
  });
  const allAnswered = questionIds.every(qid => {
    const qa = qam[qid];
    const display = qa ? getQuestionDisplayValue(qid, qa) : undefined;
    if (!display) return false;
    if (Array.isArray(display)) return display.length > 0;

    return display !== '[No response]' && String(display).trim().length > 0;
  });
  const text = renderTemplatePlain(template, qam);

  return { text, complete: allAnswered && !!text.trim() };
}

export function getFirstAnswered(
  qaMap: Record<string, QuestionAnswer>,
  primaryId: string,
  secondaryId: string
): string[] {
  const primary = qaMap?.[primaryId];

  if (Array.isArray(primary?.selectedValues) && primary.selectedValues.length > 0) {
    return primary.selectedValues as string[];
  }

  const secondary = qaMap?.[secondaryId];

  if (Array.isArray(secondary?.selectedValues) && secondary.selectedValues.length > 0) {
    return secondary.selectedValues as string[];
  }

  return [];
}

const resolveAndCheckTemplate = (
  template: string,
  questionMaps: Record<string, QuestionAnswer>[]
): { text: string; complete: boolean } => {
  if (!template) return { text: '', complete: false };
  let complete = true;

  const resolveFromMaps = (questionId: string): string => {
    for (const map of questionMaps) {
      if (!map || !map[questionId]) continue;
      const questionData = map[questionId] as QuestionAnswer;
      let valuesToUse: string[] = Array.isArray(questionData?.selectedValues)
        ? [...questionData.selectedValues]
        : [];

      if (valuesToUse.includes('other')) {
        const nonOtherValues = valuesToUse.filter(val => val !== 'other');

        if (nonOtherValues.length > 0) {
          valuesToUse = nonOtherValues;
        } else if (questionData?.otherValue && String(questionData.otherValue).trim()) {
          valuesToUse = [String(questionData.otherValue).trim()];
        } else {
          valuesToUse = ['other'];
        }
      } else if (valuesToUse.length === 0 && Array.isArray(questionData?.options)) {
        const otherOption = questionData.options.find(
          (opt: QuestionOption) => opt?.id === 'other' && opt?.selected
        );

        if (otherOption) {
          const otherVal = questionData?.otherValue && String(questionData.otherValue).trim();
          valuesToUse = [otherVal || 'other'];
        }
      }

      return valuesToUse.join(', ');
    }

    return '';
  };

  let text = template.replace(/\{([^}]+)\}/g, (_match, qid: string) => {
    const value = resolveFromMaps(qid);

    if (!value) {
      complete = false;

      return '';
    }

    // Bold the value(s) we insert
    const parts = String(value)
      .split(',')
      .map(s => s.trim())
      .filter(Boolean)
      .map(v => bold(v));

    return parts.join(', ');
  });
  text = text
    .replace(RE_DOUBLE_COMMA, ',')
    .replace(RE_TRAILING_COMMA, '')
    .replace(RE_COMMA_BEFORE_PUNCT, '$1')
    .replace(RE_WHITESPACE, ' ')
    .trim();

  return { text, complete };
};

const normalizeText = (text: string): string => {
  return text
    .replace(RE_DOUBLE_COMMA, ',')
    .replace(RE_TRAILING_COMMA, '')
    .replace(RE_COMMA_BEFORE_PUNCT, '$1')
    .replace(RE_WHITESPACE, ' ')
    .trim();
};

const cleanProcessedText = (text: string): string => {
  return normalizeText(
    text
      .replace(RE_UUID, '')
      .replace(RE_SPAN_DATA_QID, '$1')
      // Reeval normalization fixes
      .replace(/^(Provocative factors|Palliative factors):\s*/i, '')
      .replace(/Symptoms described as:?/gi, 'Symptoms:')
      .replace(/\s*described as\s*undefined/gi, '')
      .replace(/Interference with general activity:/g, 'General activity:')
      .replace('Not changing', 'not changing')
      .replace('Noise / Grinding', 'Noise/grinding')
      .replace(RE_COLON_UNDEFINED, '')
      .replace(RE_COLON_NO_RESPONSE, '')
      .replace(RE_COLON_NONE, '')
      .replace(RE_EMPTY_COLON, '')
      .replace(RE_TRAILING_DESCRIBED, '')
      .replace(RE_TRAILING_CHARACTERIZED, '')
      .replace(RE_TRAILING_PRESENTED, '')
  );
};

const isIncompleteItem = (text: string): boolean => {
  return (
    (text.includes('/10') && !text.match(/\d+\/10/)) ||
    !!text.match(/^\w+:\s*\.?$/) ||
    !!text.match(/^\w+\s+\w+:\s*\.?$/) ||
    !!text.match(/^\w+:\s*$/) ||
    text === 'Bilateral symptoms:' ||
    text.includes('undefined') ||
    text.includes('[No response]') ||
    text.includes('None') ||
    text.trim() === '' ||
    text.trim() === ':' ||
    text.trim() === 'Due to:' ||
    text.trim() === 'Symptoms' ||
    text.trim() === 'Beginning' ||
    !!text.match(/:\s*$/)
  );
};

const wrapList = (
  processedItems: string[],
  _wrapper: 'onset' | 'factors' | 'quality' | 'severity' | 'timing' | 'default',
  renderAsParagraphs: boolean = false
): string => {
  const normalized = processedItems
    .map(s => (typeof s === 'string' ? s.trim() : ''))
    .filter(s => s.length > 0);
  if (normalized.length === 0) return '';

  if (renderAsParagraphs) {
    const paragraphs = normalized.map(item => {
      if (item.startsWith('<li')) {
        const inner = item.replace(/^<li[^>]*>/, '').replace(/<\/li>$/, '');

        return `<p>${inner}</p>`;
      }

      return item.startsWith('<p>') ? item : `<p>${item}</p>`;
    });

    return paragraphs.join('\n');
  }

  return `<ul>\n${normalized.join('\n')}\n</ul>`;
};

const wrapNested = (
  nestedLis: string,
  wrapper: 'onset' | 'factors' | 'quality' | 'severity' | 'timing' | 'default',
  title?: string,
  renderAsParagraphs: boolean = false
): string => {
  const header = title ? `<div><strong>${title}</strong></div>` : '';

  if (renderAsParagraphs) {
    const items = nestedLis
      .split(/\n/)
      .map(line => line.trim())
      .filter(Boolean)
      .map(line =>
        line.startsWith('<li') ? line.replace(/^<li[^>]*>/, '').replace(/<\/li>$/, '') : line
      )
      .map(content => (content.startsWith('<p>') ? content : `<p>${content}</p>`))
      .join('\n');

    return `${header}${items}`;
  }

  return `${header}<ul>\n${nestedLis}\n</ul>`;
};

const renderListGeneric = (
  listItems: ListItem[],
  renderOne: (item: string, isNested: boolean) => string | string[],
  _wrapper: 'onset' | 'factors' | 'quality' | 'severity' | 'timing' | 'default',
  renderAsParagraphs: boolean = false
): string => {
  const processed = listItems.map(item => {
    if (typeof item === 'string') {
      const li = renderOne(item, false);

      if (Array.isArray(li)) {
        const joined = li.filter(s => typeof s === 'string' && s.trim()).join('');

        return joined;
      }

      return typeof li === 'string' && li.trim() ? li : '';
    }

    const nestedLis = (item.nested || [])
      .map(nestedItem => {
        const li = renderOne(nestedItem, true);

        if (Array.isArray(li)) {
          return li.filter(s => typeof s === 'string' && s.trim()).join('');
        }

        return typeof li === 'string' && li.trim() ? li : '';
      })
      .filter(Boolean)
      .join('\n');

    return nestedLis.trim()
      ? wrapNested(nestedLis, _wrapper, (item as any).title, renderAsParagraphs)
      : '';
  });

  return wrapList(processed, _wrapper, renderAsParagraphs);
};

const formatOnset = (item: string, isNested: boolean): string | string[] => {
  if (item.includes('Beginning') && item.includes('on') && item.includes('due to')) {
    const cleanItem = item;

    const parts = cleanItem.split(' due to ');
    const beginningPart = parts[0];
    const dueToPart = parts[1];
    const beginningMatch = beginningPart.match(/Beginning (.+) on (.+)/);

    if (beginningMatch) {
      const manner = beginningMatch[1];
      const date = beginningMatch[2];
      const indent = isNested ? ' style="margin-left: 20px; padding-left: 10px;"' : '';
      const lines: string[] = [];

      if (manner && date) {
        lines.push(
          `<li${indent} class="list-item">\nBeginning\n${bold(manner?.toLowerCase())}\n${bold(` on ${date}`)}\n</li>`
        );
      }

      if (dueToPart && String(dueToPart).trim()) {
        const innerDue = String(dueToPart).replace(/<\/?strong>/gi, '');
        const dueBold = bold(innerDue.toLowerCase());
        lines.push(`<li${indent}>\nDue to:\n${dueBold}\n</li>`);
      }

      return lines.length ? lines : '';
    }
  } else if (/^Symptoms\s*noticed\s*:\s*/i.test(item)) {
    const indent = isNested ? ' style="margin-left: 20px; padding-left: 10px;"' : '';
    const m = item.match(/^Symptoms\s*noticed\s*:\s*(.+)$/i);
    const val = (m?.[1] || '').trim();

    return `<li${indent}>Symptoms noticed: ${bold(val)}</li>`;
  } else if (/^Symptoms\s*:\s*/i.test(item)) {
    const indent = isNested ? ' style="margin-left: 20px; padding-left: 10px;"' : '';
    const m = item.match(/^Symptoms\s*:\s*(.+)$/i);
    const val = (m?.[1] || '').trim();

    return `<li${indent}>Symptoms: ${bold(val)}</li>`;
  }

  const indent = isNested ? ' style="margin-left: 20px; padding-left: 10px;"' : '';

  return `<li${indent}>${item}</li>`;
};

const splitFactors = (s: string): string[] => {
  if (s.includes(', ')) {
    return s.split(', ').reduce((acc: string[], factor: string) => {
      if (factor.includes('i.e.') && acc.length > 0) {
        acc[acc.length - 1] += ', ' + factor;
      } else {
        acc.push(factor);
      }

      return acc;
    }, []);
  }

  return [s];
};

const formatFactors = (item: string, isNested: boolean): string | string[] => {
  const indent = isNested ? ' style="margin-left: 20px; padding-left: 10px;"' : '';
  const sanitizeIE = (s: string) => s.replace(/i\.e\.,\s*/gi, 'i.e. ');
  const normalized = sanitizeIE(item);
  const allFactors = splitFactors(normalized);

  return allFactors.map(
    (factor, i) =>
      `<li${indent} key=${isNested ? 'nested-provocative-factor' : 'provocative-factor'}-${i}>${bold(
        factor
      )}</li>`
  );
};

const formatQuality = (item: string, isNested: boolean): string | string[] => {
  const indent = isNested ? ' style="margin-left: 20px; padding-left: 10px;"' : '';

  // Special handling to match legacy: collapse "Symptoms:" line with descriptors
  if (/^Symptoms:/i.test(item)) {
    const after = item.replace(/^Symptoms:\s*/i, '').trim();
    // Split by comma, keep order
    const parts = after.split(/\s*,\s*/).filter(Boolean);
    const rendered = parts
      .map(part => {
        const m = part.match(/^(.*?)(?:\s+described as\s*:?\s*(.+))?$/i);
        if (!m) return bold(part);
        const symptom = (m[1] || '').trim();
        const desc = (m[2] || '').trim();
        const validDesc = !!desc && !/^(undefined|\[No response\])$/i.test(desc);

        if (validDesc) {
          return `${bold(symptom)} described as ${bold(desc)}`;
        }

        return bold(symptom);
      })
      .join(', ');

    return `<li${indent}>
Symptoms: ${rendered}
</li>`;
  }

  if (/described as/i.test(item)) {
    const m = item.match(/^(.*?)(?:\s+described as\s*:?\s*(.*))$/i);
    const symptom = (m?.[1] || item).trim();
    const descriptionRaw = (m?.[2] || '').trim();
    const hasDesc = !!descriptionRaw && !/^(undefined|\[No response\])$/i.test(descriptionRaw);

    if (hasDesc) {
      return `<li${indent}>
${bold(symptom)}
          <span>described as </span>
${bold(descriptionRaw)}
        </li>`;
    }

    return `<li${indent}>
${bold(symptom)}
          </li>`;
  }

  return `<li${indent}>
${bold(item)}
          </li>`;
};

const formatSeverity = (item: string, isNested: boolean): string | string[] => {
  const indent = isNested ? ' style="margin-left: 20px; padding-left: 10px;"' : '';
  const replaced = item.replace(/(.+): (\d+\/10)/, `$1:\n${bold('$2')}`);

  return `<li${indent}>\n${replaced}\n</li>`;
};

const formatTiming = (item: string, isNested: boolean): string | string[] => {
  const indent = isNested ? ' style="margin-left: 20px; padding-left: 10px;"' : '';
  const replaced = item.replace(/(rarely|moderately)/g, bold('$1'));

  return `<li${indent}>\n${replaced}\n</li>`;
};

const formatDefault = (item: string, isNested: boolean): string | string[] => {
  const indent = isNested ? ' style="margin-left: 20px; padding-left: 10px;"' : '';
  const idx = item.indexOf(':');

  if (idx !== -1) {
    const label = item.slice(0, idx);
    const value = item.slice(idx + 1);
    const lv = labelValue(label, value);

    if (lv) {
      return `<li${indent}>${lv}</li>`;
    }
  }

  return `<li${indent}>${item}</li>`;
};

type MinimalQA = { selectedValues?: string[] };

const shouldSkipConditionalItem = (
  item: SummaryItem,
  section: SummarySection,
  overallQuestionAnswerMap?: Record<string, MinimalQA>
): boolean => {
  if (!isTextItem(item)) return false;
  const maybeText = item;
  if (!maybeText?.if?.id) return false;
  const contextMap: Record<string, MinimalQA> = {
    ...(section?.mainQuestionAnswerMap || {}),
    ...(overallQuestionAnswerMap || {}),
  } as Record<string, MinimalQA>;

  return !checkIfCondition(maybeText.if as any, contextMap as any);
};

const extractYellowFlags = (item: QAHolder): string[] => {
  const flags: string[] = [];

  if (item.questionAnswerMap) {
    Object.values(item.questionAnswerMap).forEach(questionData => {
      const sel = (questionData as QuestionAnswer)?.selectedValues;

      if (Array.isArray(sel) && sel.length > 0) {
        flags.push(...sel);
      }
    });
  }

  return flags;
};

const buildOnsetHTML = (
  listItems: (string | { nested: string[] })[],
  renderAsParagraphs: boolean
): string => {
  return renderListGeneric(listItems, formatOnset, 'onset', renderAsParagraphs);
};

const buildFactorsHTML = (
  listItems: (string | { nested: string[] })[],
  renderAsParagraphs: boolean
): string => {
  const normalize = (item: string | { nested: string[] }) => {
    const fix = (s: string) => s.replace(/i\.e\.,\s*/gi, 'i.e. ');
    if (typeof item === 'string') return fix(item);
    if (item && Array.isArray(item.nested)) return { nested: item.nested.map(fix) };

    return item;
  };

  const normalized = listItems.map(normalize);

  return renderListGeneric(normalized, formatFactors, 'factors', renderAsParagraphs);
};

const buildQualityHTML = (
  listItems: (string | { nested: string[] })[],
  renderAsParagraphs: boolean
): string => {
  // If there's a Symptoms: line, coalesce related descriptors into one legacy-style line
  const flat: string[] = listItems
    .map(it => (typeof it === 'string' ? it : (it?.nested || []).join(', ')))
    .filter(Boolean) as string[];

  const symptomsIdx = flat.findIndex(s => /^Symptoms:/i.test(s));

  if (symptomsIdx !== -1) {
    const base = flat[symptomsIdx];
    const after = base.replace(/^Symptoms:\s*/i, '').trim();
    const baseTokens = after
      .split(/\s*,\s*/)
      .map(t => t.replace(/Noise\/grinding/i, 'Noise / Grinding'))
      .filter(Boolean);

    // Collect descriptors from other lines
    const descriptors: Record<string, string[]> = {};

    const addDesc = (key: string, val: string) => {
      const v = val.replace(/\s*described as\s*undefined/gi, '').trim();
      if (!v) return;
      if (!descriptors[key]) descriptors[key] = [];
      descriptors[key].push(...v.split(/\s*,\s*/).filter(Boolean));
    };

    for (const s of flat) {
      const m1 = s.match(/^Pain(?:\s*\/\s*Discomfort)?\s*described as[:]?\s*(.+)$/i);
      if (m1) addDesc('Pain / Discomfort', m1[1]);
      const m2 = s.match(/^Altered sensation\s*described as[:]?\s*(.+)$/i);
      if (m2) addDesc('Altered sensation', m2[1]);
      const m3 = s.match(/^Weakness\s*described as[:]?\s*(.+)$/i);
      if (m3) addDesc('Weakness', m3[1]);

      // Single-symptom noise/grinding line
      if (/^Noise\s*\/\s*Grinding$/i.test(s) || /^Noise\/grinding$/i.test(s)) {
        if (!baseTokens.some(t => /Noise\s*\/\s*Grinding/i.test(t))) {
          baseTokens.push('Noise / Grinding');
        }
      }
    }

    // Rebuild tokens replacing with described-as when present
    const mergedTokens = baseTokens.map(token => {
      const key = /Noise\s*\/\s*Grinding/i.test(token)
        ? 'Noise / Grinding'
        : /Pain/i.test(token)
          ? 'Pain / Discomfort'
          : /Altered sensation/i.test(token)
            ? 'Altered sensation'
            : /Weakness/i.test(token)
              ? 'Weakness'
              : token;
      const descs = descriptors[key];

      if (descs && descs.length) {
        return `${bold(key)} described as ${bold(descs.join(', '))}`;
      }

      return bold(key);
    });

    const li = `<li>${`Symptoms: ${mergedTokens.join(', ')}`}</li>`;

    return `<ul>\n${li}\n</ul>`;
  }

  return renderListGeneric(listItems, formatQuality, 'quality', renderAsParagraphs);
};

const buildSeverityHTML = (
  listItems: (string | { nested: string[] })[],
  renderAsParagraphs: boolean
): string => {
  return renderListGeneric(listItems, formatSeverity, 'severity', renderAsParagraphs);
};

const buildTimingHTML = (
  listItems: (string | { nested: string[] })[],
  renderAsParagraphs: boolean
): string => {
  return renderListGeneric(listItems, formatTiming, 'timing', renderAsParagraphs);
};

const buildDefaultHTML = (
  listItems: (string | { nested: string[] })[],
  renderAsParagraphs: boolean
): string => {
  return renderListGeneric(listItems, formatDefault, 'default', renderAsParagraphs);
};

const buildAdditionalInfoHTML = (listItems: (string | { nested: string[] })[]): string => {
  const toLi = (s: string, indent = false) => {
    if (!s) return '';
    const idx = s.indexOf(':');

    if (idx === -1) {
      return `<li${indent ? ' style="margin-left: 20px; padding-left: 10px;"' : ''}>${s}</li>`;
    }

    const label = s.slice(0, idx + 1);
    const value = s.slice(idx + 1).trim();
    if (!value) return '';

    return `<li${indent ? ' style="margin-left: 20px; padding-left: 10px;"' : ''}>${label} ${bold(value)}</li>`;
  };

  const processedItems = listItems
    .map((item: ListItem) => {
      if (typeof item === 'string') {
        return toLi(item);
      }

      if (item?.nested) {
        const nestedLis = item.nested
          .map((n: string) => toLi(n, true))
          .filter(Boolean)
          .join('\n    ');

        return nestedLis ? `<ul>\n    ${nestedLis}\n  </ul>` : '';
      }

      return '';
    })
    .filter(Boolean);
  const normalized = processedItems
    .map(s => (typeof s === 'string' ? s.trim() : ''))
    .filter(s => s.length > 0);
  if (!normalized.length) return '';

  return `<ul>
    ${normalized.join('\n    ')}
  </ul>`;
};

const sectionRenderers: Record<
  string,
  (items: (string | { nested: string[] })[], renderAsParagraphs: boolean) => string
> = {
  Onset: buildOnsetHTML,
  'Provocative factors': buildFactorsHTML,
  'Palliative factors': buildFactorsHTML,
  Quality: buildQualityHTML,
  Severity: buildSeverityHTML,
  Timing: buildTimingHTML,
  Notes: () => '',
  'Additional information': (items, _p) => buildAdditionalInfoHTML(items),
};

const buildSectionHTML = (
  sectionTitle: string,
  listItems: (string | { nested: string[] })[],
  renderAsParagraphs: boolean = false
): string => {
  const renderer = sectionRenderers[sectionTitle] || buildDefaultHTML;

  return renderer(listItems, renderAsParagraphs);
};

const processComplaintSummarySection = (
  section: SummarySection,
  overallQuestionAnswerMap?: { [questionId: string]: QuestionAnswer },
  renderAsParagraphs?: boolean
): { title: string; html: string; yellowFlags?: string[] } => {
  if (!section.items || !Array.isArray(section.items)) {
    return { title: section.title, html: '' };
  }

  if (section.title === SectionTitles.Timing) {
    const anyIncomplete = section.items.some((item: SummaryItem) => {
      if (!(item as TextItem)?.template) return true;
      const { complete } = resolveAndCheckTemplate((item as TextItem).template, [
        (item as TextItem).questionAnswerMap || {},
        section.mainQuestionAnswerMap || {},
      ]);

      return !complete;
    });
    if (anyIncomplete) return { title: section.title, html: '' };
  }

  const yellowFlags: string[] = [];
  const listItems: (string | { nested: string[] })[] = [];
  const pendingOnsetNested: string[] = [];
  let onsetDueRendered = false;
  section.items.forEach(item => {
    if (
      (item as TextItem & { hide?: boolean }).hide ||
      shouldSkipConditionalItem(item, section, overallQuestionAnswerMap)
    ) {
      return;
    }

    if ((item as TextItem).type === 'section' && Array.isArray((item as SummarySection).items)) {
      const nested = item as SummarySection;
      const nestedItems: string[] = [];

      // Gate nested accident/work sections by cause so only one shows
      if (
        nested.title === SectionTitles.AutoAccidentDetails ||
        nested.title === SectionTitles.WorkInjuryDetails
      ) {
        const mergedForGate: Record<string, QuestionAnswer> = {
          ...(section.mainQuestionAnswerMap || {}),
          ...(isTextItem(item) ? (item as TextItem).questionAnswerMap || {} : {}),
          ...((overallQuestionAnswerMap || {}) as Record<string, QuestionAnswer>),
        };
        const causeVals = getFirstAnswered(
          mergedForGate,
          CCSPrimaryQuestionIds.causedQuestionId,
          CCSSecondaryQuestionIds.causedQuestionId
        );
        const isAuto = causeVals.includes('Auto accident');
        const isWork = causeVals.includes('Employment-related injury');

        if (
          (nested.title === SectionTitles.AutoAccidentDetails && !isAuto) ||
          (nested.title === SectionTitles.WorkInjuryDetails && !isWork)
        ) {
          return; // Skip processing this nested section entirely
        }
      }

      (item as SummarySection).items?.forEach((nestedItem: SummaryItem) => {
        if ((nestedItem as TextItem & { hide?: boolean }).hide) {
          return;
        }

        if (isTextItem(nestedItem)) {
          // Exclude attorney-related items from subjective builder
          const nestedTpl = String(((nestedItem as TextItem).template || '') as string);
          const isAttorneyNested =
            nestedTpl.includes(CCSPrimaryQuestionIds.attorneyInjuryQuestionId) ||
            nestedTpl.includes(CCSSecondaryQuestionIds.attorneyInjuryQuestionId) ||
            nestedTpl.includes(CCSPrimaryQuestionIds.attorneyNameInjuryQuestionId) ||
            nestedTpl.includes(CCSSecondaryQuestionIds.attorneyNameInjuryQuestionId);
          // Exclude employer contact info from subjective builder
          const isEmployerContactNested =
            nestedTpl.includes(CCSPrimaryQuestionIds.contactPersonInfoQuestionId) ||
            nestedTpl.includes(CCSSecondaryQuestionIds.contactPersonInfoQuestionId);
          if (isAttorneyNested || isEmployerContactNested) return;

          if (
            section.title === SectionTitles.Onset ||
            section.title === SectionTitles.AdditionalInformation
          ) {
            // Suppress redundant accident type line; type is shown inline on the Due to line
            if (/^Accident type:/i.test((nestedItem as TextItem).template || '')) {
              return;
            }

            if (/Status at time of impact:/i.test((nestedItem as TextItem).template)) {
              const mergedQAMEarly: Record<string, QuestionAnswer> = {
                ...(section.mainQuestionAnswerMap || {}),
                ...(isTextItem(item) ? (item as TextItem).questionAnswerMap || {} : {}),
                ...(isTextItem(nestedItem) ? (nestedItem as TextItem).questionAnswerMap || {} : {}),
              };
              const lookEarly =
                mergedQAMEarly[CCSPrimaryQuestionIds.impactLookingQuestionId]?.selectedValues ||
                mergedQAMEarly[CCSSecondaryQuestionIds.impactLookingQuestionId]?.selectedValues ||
                [];
              const wereYouEarly =
                mergedQAMEarly[CCSPrimaryQuestionIds.autoWereYouQuestionId]?.selectedValues?.[0] ||
                mergedQAMEarly[CCSSecondaryQuestionIds.autoWereYouQuestionId]
                  ?.selectedValues?.[0] ||
                '';
              const seatbeltValEarly =
                mergedQAMEarly[CCSPrimaryQuestionIds.seatbeltQuestionId]?.selectedValues?.[0] ||
                mergedQAMEarly[CCSSecondaryQuestionIds.seatbeltQuestionId]?.selectedValues?.[0] ||
                '';
              const airbagValEarly =
                mergedQAMEarly[CCSPrimaryQuestionIds.airbagQuestionId]?.selectedValues?.[0] ||
                mergedQAMEarly[CCSSecondaryQuestionIds.airbagQuestionId]?.selectedValues?.[0] ||
                '';
              const seatbeltPhraseEarly =
                seatbeltValEarly === 'Yes' || seatbeltValEarly === 'No'
                  ? convertYesNoToDescriptive(
                      CCSPrimaryQuestionIds.seatbeltQuestionId,
                      String(seatbeltValEarly),
                      'Seatbelt'
                    )
                  : '';
              const airbagPhraseEarly =
                airbagValEarly === 'Yes' || airbagValEarly === 'No'
                  ? convertYesNoToDescriptive(
                      CCSPrimaryQuestionIds.airbagQuestionId,
                      String(airbagValEarly),
                      'Airbag'
                    )
                  : '';
              const lookingPhraseEarly = lookEarly.length
                ? `looking ${lookEarly.map(v => String(v).toLowerCase()).join(', ')}`
                : '';
              const partsEarly = [
                lookingPhraseEarly,
                wereYouEarly,
                seatbeltPhraseEarly,
                airbagPhraseEarly,
              ].filter(Boolean);
              const plainEarly = partsEarly.length
                ? `Status at time of impact: <strong>${partsEarly.join(', ')}</strong>`
                : '';
              const processedTextEarly = cleanProcessedText(plainEarly);
              if (!processedTextEarly || isIncompleteItem(processedTextEarly)) return;
              nestedItems.push(processedTextEarly);

              return;
            }

            if (/Vehicle drivable:/i.test((nestedItem as TextItem).template)) {
              const mergedQAMEarly: Record<string, QuestionAnswer> = {
                ...(section.mainQuestionAnswerMap || {}),
                ...(isTextItem(item) ? (item as TextItem).questionAnswerMap || {} : {}),
                ...(isTextItem(nestedItem) ? (nestedItem as TextItem).questionAnswerMap || {} : {}),
              };
              const vEarly =
                mergedQAMEarly[CCSPrimaryQuestionIds.vehicleDrivableQuestionId]
                  ?.selectedValues?.[0] ||
                mergedQAMEarly[CCSSecondaryQuestionIds.vehicleDrivableQuestionId]
                  ?.selectedValues?.[0] ||
                '';

              if (vEarly === 'Yes' || vEarly === 'No') {
                const phraseEarly = convertYesNoToDescriptive(
                  CCSPrimaryQuestionIds.vehicleDrivableQuestionId,
                  String(vEarly),
                  'Vehicle drivable'
                );
                const plainEarly = `Vehicle drivable: ${bold(phraseEarly)}`;
                const processedTextEarly = cleanProcessedText(plainEarly);
                if (!processedTextEarly || isIncompleteItem(processedTextEarly)) return;
                nestedItems.push(processedTextEarly);

                return;
              }

              return;
            }

            // Render Currently off work strictly as Yes/No (no descriptive text)
            if (/^Currently\s+off\s+work:/i.test((nestedItem as TextItem).template)) {
              const mergedQAMEarly: Record<string, QuestionAnswer> = {
                ...(section.mainQuestionAnswerMap || {}),
                ...(isTextItem(item) ? (item as TextItem).questionAnswerMap || {} : {}),
                ...(isTextItem(nestedItem) ? (nestedItem as TextItem).questionAnswerMap || {} : {}),
              };

              const val =
                mergedQAMEarly[CCSPrimaryQuestionIds.currentlyOffWorkInjuryQuestionId]
                  ?.selectedValues?.[0] ||
                mergedQAMEarly[CCSSecondaryQuestionIds.currentlyOffWorkInjuryQuestionId]
                  ?.selectedValues?.[0] ||
                '';

              if (val === 'Yes' || val === 'No') {
                const plainEarly = `Currently off work: ${bold(val)}`;
                const processedTextEarly = cleanProcessedText(plainEarly);
                if (!processedTextEarly || isIncompleteItem(processedTextEarly)) return;
                nestedItems.push(processedTextEarly);

                return;
              }
            }

            const mergedQAMEarly: Record<string, QuestionAnswer> = {
              ...(section.mainQuestionAnswerMap || {}),
              ...(isTextItem(nestedItem) ? (nestedItem as TextItem).questionAnswerMap || {} : {}),
            };
            const resultResolved = resolveWithDisplay(
              isTextItem(nestedItem) ? (nestedItem as TextItem).template : '',
              mergedQAMEarly
            );
            const text = resultResolved.text;
            const isComplete = resultResolved.complete;
            if (!isComplete) return;
            const processedText = cleanProcessedText(text);
            if (!processedText || isIncompleteItem(processedText)) return;
            nestedItems.push(processedText);

            return;
          }

          const mergedQAM: Record<string, QuestionAnswer> = {
            ...(section.mainQuestionAnswerMap || {}),
            ...(isTextItem(item) ? (item as TextItem).questionAnswerMap || {} : {}),
            ...(isTextItem(nestedItem) ? (nestedItem as TextItem).questionAnswerMap || {} : {}),
          };
          let plain = '';

          if (/Status at time of impact:/i.test((nestedItem as TextItem).template)) {
            const look =
              mergedQAM[CCSPrimaryQuestionIds.impactLookingQuestionId]?.selectedValues ||
              mergedQAM[CCSSecondaryQuestionIds.impactLookingQuestionId]?.selectedValues ||
              [];
            const wereYou =
              mergedQAM[CCSPrimaryQuestionIds.autoWereYouQuestionId]?.selectedValues?.[0] ||
              mergedQAM[CCSSecondaryQuestionIds.autoWereYouQuestionId]?.selectedValues?.[0] ||
              '';
            const seatbeltVal =
              mergedQAM[CCSPrimaryQuestionIds.seatbeltQuestionId]?.selectedValues?.[0] ||
              mergedQAM[CCSSecondaryQuestionIds.seatbeltQuestionId]?.selectedValues?.[0] ||
              '';
            const airbagVal =
              mergedQAM[CCSPrimaryQuestionIds.airbagQuestionId]?.selectedValues?.[0] ||
              mergedQAM[CCSSecondaryQuestionIds.airbagQuestionId]?.selectedValues?.[0] ||
              '';
            const seatbeltPhrase =
              seatbeltVal === 'Yes' || seatbeltVal === 'No'
                ? convertYesNoToDescriptive(
                    CCSPrimaryQuestionIds.seatbeltQuestionId,
                    String(seatbeltVal),
                    'Seatbelt'
                  )
                : '';
            const airbagPhrase =
              airbagVal === 'Yes' || airbagVal === 'No'
                ? convertYesNoToDescriptive(
                    CCSPrimaryQuestionIds.airbagQuestionId,
                    String(airbagVal),
                    'Airbag'
                  )
                : '';
            const lookingPhrase = look.length ? `looking ${look.join(', ')}` : '';
            const parts = [lookingPhrase, wereYou, seatbeltPhrase, airbagPhrase].filter(Boolean);
            plain = parts.length ? `Status at time of impact: ${parts.join(', ')}` : '';
          } else if (/Vehicle drivable:/i.test((nestedItem as TextItem).template)) {
            const v =
              mergedQAM[CCSPrimaryQuestionIds.vehicleDrivableQuestionId]?.selectedValues?.[0] ||
              mergedQAM[CCSSecondaryQuestionIds.vehicleDrivableQuestionId]?.selectedValues?.[0] ||
              '';

            if (v === 'Yes' || v === 'No') {
              const phrase = convertYesNoToDescriptive(
                CCSPrimaryQuestionIds.vehicleDrivableQuestionId,
                String(v),
                'Vehicle drivable'
              );
              plain = `Vehicle drivable: ${phrase}`;
            } else {
              plain = '';
            }
          } else {
            plain = renderTemplatePlain((nestedItem as TextItem).template, mergedQAM);
          }

          const processedText = cleanProcessedText(plain);

          if (!processedText || isIncompleteItem(processedText)) {
            return;
          }

          nestedItems.push(processedText);
        }
      });

      if (nestedItems.length > 0) {
        if (section.title === SectionTitles.Onset) {
          if (onsetDueRendered) {
            listItems.push({ nested: nestedItems });
          } else {
            pendingOnsetNested.push(...nestedItems);
          }
        } else {
          listItems.push({ nested: nestedItems });
        }
      }
    } else if (isTextItem(item)) {
      // Exclude attorney-related items from subjective builder
      const topTpl = String(((item as TextItem).template || '') as string);
      const isAttorneyTop =
        topTpl.includes(CCSPrimaryQuestionIds.attorneyInjuryQuestionId) ||
        topTpl.includes(CCSSecondaryQuestionIds.attorneyInjuryQuestionId) ||
        topTpl.includes(CCSPrimaryQuestionIds.attorneyNameInjuryQuestionId) ||
        topTpl.includes(CCSSecondaryQuestionIds.attorneyNameInjuryQuestionId);
      // Exclude employer contact info from subjective builder
      const isEmployerContactTop =
        topTpl.includes(CCSPrimaryQuestionIds.contactPersonInfoQuestionId) ||
        topTpl.includes(CCSSecondaryQuestionIds.contactPersonInfoQuestionId);
      if (isAttorneyTop || isEmployerContactTop) return;

      if (
        section.title === SectionTitles.Onset ||
        section.title === SectionTitles.AdditionalInformation
      ) {
        const mergedForItem: Record<string, QuestionAnswer> = {
          ...((section.mainQuestionAnswerMap || {}) as Record<string, QuestionAnswer>),
          ...(((item as TextItem).questionAnswerMap || {}) as Record<string, QuestionAnswer>),
          ...(((overallQuestionAnswerMap || {}) as unknown as Record<string, QuestionAnswer>) ||
            {}),
        };
        const { text, complete } = resolveWithDisplay((item as TextItem).template, mergedForItem);
        if (!complete) return;
        const processedText = cleanProcessedText(text);
        if (!processedText || isIncompleteItem(processedText)) return;

        if ((item as TextItem).template.includes('due to')) {
          onsetDueRendered = true;
        }

        // Inject accident type immediately after Auto accident on the "Due to" line
        if (
          /Beginning/i.test((item as TextItem).template || '') &&
          processedText.includes('due to')
        ) {
          let updated = processedText;
          const causeVals = getFirstAnswered(
            mergedForItem,
            CCSPrimaryQuestionIds.causedQuestionId,
            CCSSecondaryQuestionIds.causedQuestionId
          );
          const isAuto = causeVals.includes('Auto accident');

          if (isAuto) {
            const qaTypePrimaryId = CCSPrimaryQuestionIds.descriptionOfAccidentQuestionId;
            const qaTypeSecondaryId = CCSSecondaryQuestionIds.descriptionOfAccidentQuestionId;
            const qaType = mergedForItem[qaTypePrimaryId] || mergedForItem[qaTypeSecondaryId];
            let typeStr = '';

            if (qaType) {
              if (Array.isArray(qaType.selectedValues) && qaType.selectedValues[0]) {
                typeStr = String(qaType.selectedValues[0]);
              } else {
                const disp = getQuestionDisplayValue(
                  qaType === mergedForItem[qaTypePrimaryId] ? qaTypePrimaryId : qaTypeSecondaryId,
                  qaType as QuestionAnswer
                );

                if (disp && disp !== '[No response]') {
                  typeStr = Array.isArray(disp)
                    ? String((disp as string[])[0] || '')
                    : String(disp);
                }
              }
            }

            if (typeStr) {
              const lower = updated.toLowerCase();
              const dueIdx = lower.indexOf('due to');

              if (dueIdx !== -1) {
                const closeIdx = updated.indexOf('</strong>', dueIdx);

                if (closeIdx !== -1) {
                  updated = `${updated.slice(0, closeIdx + 9)}, ${bold(typeStr)}${updated.slice(
                    closeIdx + 9
                  )}`;
                } else {
                  updated = updated.replace(
                    /(due to\s+)([^<,]+)/i,
                    (_m: string, p1: string, p2: string) => `${p1}${p2}, ${bold(typeStr)}`
                  );
                }
              }
            }
          }

          listItems.push(updated);
        } else {
          listItems.push(processedText);
        }

        if ((item as TextItem).template.includes('due to')) {
          if (pendingOnsetNested.length > 0) {
            listItems.push({ nested: pendingOnsetNested });
            pendingOnsetNested.length = 0;
          }
        }

        return;
      }

      if (section.title === SectionTitles.GeneralQuestions) {
        const mergedForItem: Record<string, QuestionAnswer> = {
          ...((section.mainQuestionAnswerMap || {}) as Record<string, QuestionAnswer>),
          ...(((item as TextItem).questionAnswerMap || {}) as Record<string, QuestionAnswer>),
        };
        const { text, complete } = resolveWithDisplay((item as TextItem).template, mergedForItem);
        if (!complete) return;
        const processedText = cleanProcessedText(text);
        if (!processedText || isIncompleteItem(processedText)) return;
        listItems.push(processedText);

        return;
      }

      const templateStr = (item as any).template;
      const { text, complete } = resolveAndCheckTemplate(templateStr, [
        ((item as any as TextItem).questionAnswerMap || {}) as Record<string, QuestionAnswer>,
        (section.mainQuestionAnswerMap || {}) as Record<string, QuestionAnswer>,
      ]);

      if (!complete && section.title !== SectionTitles.Quality) return;

      const processedText = cleanProcessedText(text);
      if (!processedText || isIncompleteItem(processedText)) return;

      if (section.title === SectionTitles.YellowFlags) {
        yellowFlags.push(
          ...extractYellowFlags({
            questionAnswerMap: ((item as any as TextItem).questionAnswerMap || {}) as Record<
              string,
              QuestionAnswer
            >,
          })
        );
      } else {
        listItems.push(processedText);
      }
    }
  });
  const finalRenderAsParagraphs =
    typeof renderAsParagraphs === 'boolean'
      ? renderAsParagraphs
      : (overallQuestionAnswerMap as any)?.__renderAsParagraphs === true;
  const html =
    listItems.length > 0 ? buildSectionHTML(section.title, listItems, finalRenderAsParagraphs) : '';

  return {
    title: section.title,
    html,
    ...(yellowFlags.length > 0 && { yellowFlags }),
  };
};

export function renderInterimSections(
  sections: SummarySection[] = [],
  qaMap: Record<string, QuestionAnswer> = {}
): string {
  const htmlParts: string[] = [];

  // Enforce V1 section order regardless of incoming array order
  const orderMap: Record<string, number> = {
    Status: 0,
    [SectionTitles.SymptomSpecifics]: 1,
    Timing: 2,
  };
  const indexed = sections.map((s, i) => ({ s, i }));
  const ordered = indexed
    .filter(({ s }) => s?.title !== SectionTitles.GeneralQuestions)
    .sort((a, b) => {
      const ap = orderMap[a.s?.title as string] ?? 1000 + a.i;
      const bp = orderMap[b.s?.title as string] ?? 1000 + b.i;

      return ap - bp || a.i - b.i;
    })
    .map(({ s }) => s);

  for (const section of ordered) {
    const processed = processComplaintSummarySection(section, qaMap, true);
    const html = processed?.html || '';

    if (html && html.trim()) {
      htmlParts.push(html);
    }
  }

  return htmlParts.join('');
}

export function buildSharedInterimBlock(shared?: {
  newComplaintValue?: string;
  newComplaintDetails?: string;
}): string {
  if (!shared || typeof shared.newComplaintValue === 'undefined') return '';
  const value = String(shared.newComplaintValue);

  if (value === 'Yes') {
    const detailsLine = shared.newComplaintDetails
      ? `<p>New complaint details: ${bold(shared.newComplaintDetails)}</p>`
      : '';

    return `
<p>&nbsp;</p>
<p>New complaint: The patient ${bold('reported')} a ${bold('new problem')} since the last visit.</p>
${detailsLine}
`;
  }

  if (value === 'No') {
    return `
<p>&nbsp;</p>
<p>New complaint: The patient ${bold('denies')} any significant ${bold('new problems')} since the prior assessment.</p>
`;
  }

  // Custom surveys can relabel the Yes/No options; show the answer as given.
  if (value) {
    const detailsLine = shared.newComplaintDetails
      ? `<p>New complaint details: ${bold(escapeHtml(shared.newComplaintDetails))}</p>`
      : '';

    return `
<p>&nbsp;</p>
<p>New complaint: ${bold(escapeHtml(value))}</p>
${detailsLine}
`;
  }

  return '';
}

export function buildSharedExerciseBlock(exerciseData?: {
  frequency?: string;
  impediments?: string[];
  adlImplementation?: string;
}): string {
  if (!exerciseData || !exerciseData.frequency) return '';

  const freq = exerciseData.frequency;
  const impediments = exerciseData.impediments || [];
  const adlImplementation = exerciseData.adlImplementation;

  let exerciseHtml = `<p>&nbsp;</p>`;

  if (freq === 'Have not received the exercises') {
    exerciseHtml += `<p>Exercise performance: ${bold(freq)}.</p>`;
  } else if (freq === 'Consistently' || freq === 'Usually') {
    exerciseHtml += `<p>Exercise performance: ${bold(freq)} doing exercises.</p>`;
  } else if ((freq === 'Occasionally' || freq === 'Not at all') && impediments.length > 0) {
    const dueTo = impediments.join(', ');
    exerciseHtml += `<p>Exercise performance: ${bold(freq)} started exercises, due to ${bold(dueTo)}.</p>`;
  } else if (freq) {
    exerciseHtml += `<p>Exercise performance: ${bold(freq)}.</p>`;
  }

  if (adlImplementation) {
    exerciseHtml += `<p>ADL implementation: ${bold(adlImplementation)}</p>`;
  }

  return exerciseHtml;
}

export const buildCCSProblem = ({
  problem,
  problemNumber,
  totalProblems,
}: {
  problem?: string;
  problemNumber: number;
  totalProblems: number;
}) => {
  if (!problem) return '';

  return `<div>
        <span>
        <h3>
            ${problem?.slice(0, 1).toUpperCase()}${problem?.slice(1)}
        </h3>${problemNumber && totalProblems ? `Complaint ${problemNumber} of ${totalProblems}` : ''}
        </span><br>
    </div>`;
};

export const buildCCSContentDetails = ({
  sections,
  skipYellowFlags = false,
  suppressSectionHeaders = false,
  suppressBullets = false,
}: {
  sections: any[];
  skipYellowFlags?: boolean;
  suppressSectionHeaders?: boolean;
  suppressBullets?: boolean;
}) => {
  return sections.reduce((str, section) => {
    if (skipYellowFlags && section.title === SectionTitles.YellowFlags) {
      return str;
    }

    if (section?.html && section.html.trim()) {
      const headerTitle =
        section.title === SectionTitles.AdditionalInformation
          ? SectionTitles.AdditionalInformation
          : section.title;

      if (!suppressSectionHeaders && section.title !== SectionTitles.YellowFlags) {
        str += `<div><strong>${headerTitle}</strong></div>`;
      }

      if (section.title !== 'Yellow flags') {
        const contentHtml = suppressBullets
          ? section.html
              .replace(/<ul>/g, '')
              .replace(/<\/ul>/g, '')
              .replace(/<li[^>]*>/g, '<p>')
              .replace(/<\/li>/g, '</p>')
          : section.html;
        str += contentHtml;
      }
    }

    return str;
  }, '');
};

// Pregnancy and Yellow Flags helpers

export interface PregnancyData {
  statuses: string[];
  dueDates: string[];
  lmpDates: string[];
}

export function renderPregnancyHtml(pregnancy: PregnancyData): string {
  const uniqueStatuses = (pregnancy.statuses || []).filter(Boolean);
  const uniqueDueDates = (pregnancy.dueDates || []).filter(Boolean);
  const uniqueLmpDates = (pregnancy.lmpDates || []).filter(Boolean);

  const items: string[] = [];

  if (uniqueStatuses.length) {
    const status = uniqueStatuses[0];
    const isPregnant =
      status === 'Yes (confirmed due date)' || status === 'Possibly (unconfirmed/suspected)';

    if (isPregnant) {
      items.push(`<li>Pregnancy status: ${bold(status)}</li>`);
    }
  }

  if (items.length) {
    const status = uniqueStatuses[0] || '';

    if (status === 'Possibly (unconfirmed/suspected)') {
      if (uniqueLmpDates.length) {
        items.push(
          `<li>Date of last menstrual period (approximate): ${bold(uniqueLmpDates[0])}</li>`
        );
      }
    } else if (status === 'Yes (confirmed due date)') {
      if (uniqueDueDates.length) {
        items.push(`<li>Due date: ${bold(uniqueDueDates[0])}</li>`);
      }
    }
  }

  if (!items.length) return '';

  return `
<p><br class="ProseMirror-trailingBreak"><strong>Pregnancy</strong></p>
<ul>
  ${items.join('')}
</ul>
`;
}

export function renderYellowFlagsHtml(flags: string[]): string {
  const unique = Array.from(new Set((flags || []).map(f => `${f}`.trim()))).filter(Boolean);
  if (!unique.length) return '';

  return `
<p><br class="ProseMirror-trailingBreak"><strong>Yellow flags</strong></p>
<ul>
  ${unique.map(flag => `<li>${flag}</li>`).join('')}
</ul>
`;
}
